import clsx from "clsx";
import {useCallback, useMemo, useState} from "react";
import {Col, Modal, Row} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import ModalBody from "../../modals-new/ModalBody";
import ModalFooter from "../../modals-new/ModalFooter";
import ModalHeader from "../../modals-new/ModalHeader";
import {
    getCategoricalColor,
    getCategoricalNeutral,
} from "../../../utils/ColorUtils";
import Checkbox from "../checkbox/Checkbox";
import InfoBox from "../info-box/InfoBox";
import Input from "../input/Input";
import LabeledInput from "../input/labeled-input/LabeledInput";
import LabeledSelect from "../select/labeled-select/LabeledSelect";
import TextSwitch from "../text-switch/TextSwitch";
import {
    DistributionChart,
    DistributionArea,
    DistributionWarnings,
    EachValuePreview,
    ParentLegend,
    ParentSwitch,
    Suggestions,
} from "./AsyncTableGroupDistribution";
import {
    formatColumnWarning,
    formatGroupLabel,
    formatParentGroupLabel,
    formatPreviewWarning,
    getUsableSuggestions,
} from "./asyncTableDistribution";
import {
    buildGroupLevel,
    buildGroupSort,
    DATE_UNIT_LABELS,
    DATE_UNITS,
    GROUP_SORT_ICONS,
    GROUP_SORT_LABELS,
    GROUP_SORTS,
    isDateLevel,
    MAX_RULE_GROUPS,
    normalizeDateUnit,
    normalizeGroupSort,
    parseGroupRule,
    serializeGroupBuckets,
} from "./asyncTableGroupRules";
import useGroupDistribution from "./useGroupDistribution";
import "./_async-table-group-rule-modal.scss";

const MODE_EACH = "each";
const MODE_CUSTOM = "custom";

const ERROR_KEYS = {
    invalid: "groupRuleErrorInvalid",
    wildcardNotAlone: "groupRuleErrorWildcard",
    tooMany: "groupRuleErrorTooMany",
};

// kolejność grup z ikoną (ta sama co w nagłówku kolumny poziomu)
const renderSortOption = (option) => (
    <span>
        <i
            className={`fa-solid ${GROUP_SORT_ICONS[option.value]} fa-fw me-2`}
            aria-hidden="true"
        />
        {option.name}
    </span>
);

const EMPTY_GROUP_FIELDS = [];

// informacje w modalu (opis trybu, podpowiedzi zamiast pustej listy)
function RuleInfo({text}) {
    return (
        <InfoBox className="async-table-group-rule-info">
            <i className="fa-solid fa-circle-info me-2" aria-hidden="true" />
            <span>{text}</span>
        </InfoBox>
    );
}

/**
 * Kółko z kolorem grupy - ten sam kolor co na histogramie. Przedziały w kolejności palety kategorii,
 * "Pozostałe" szare, puste wartości - pusta szara obwódka (brak danych, nie kolejna kategoria).
 * @param {"bucket"|"rest"|"empty"} kind
 * @param {number} [index] indeks przedziału
 */
function GroupSwatch({kind, index}) {
    const color =
        kind === "bucket"
            ? getCategoricalColor(index)
            : getCategoricalNeutral();
    return (
        <span
            className={`async-table-group-rule-swatch ${kind === "bucket" ? `color-${index + 1}` : kind}`}
            style={
                kind === "empty" ? {borderColor: color} : {background: color}
            }
            aria-hidden="true"
        />
    );
}

/**
 * Wiersz podglądu: etykieta grupy, liczba wierszy z podglądu API (po prawej), pod spodem nazwa grupy.
 * @param {Object} [props.group] grupa z `Preview.Groups` (count, overlap, subgroups)
 * @param {string[]} [props.warnings] ostrzeżenia tej grupy - ikona z podpowiedzią
 */
function PreviewGroup({
    label,
    name,
    onNameChange,
    placeholder,
    muted,
    swatch,
    group,
    warnings,
}) {
    const {t} = useTranslation();
    const warningText = warnings?.join(" ");
    return (
        <li className={clsx({muted, empty: group?.count === 0})}>
            <div className="async-table-group-rule-group-head">
                {swatch}
                <b>{label}</b>
                {warningText && (
                    <i
                        className="fa-solid fa-triangle-exclamation async-table-group-rule-group-warning"
                        title={warningText}
                        aria-label={warningText}
                        role="img"
                    />
                )}
                <span className="async-table-group-rule-group-count">
                    {group && t("groupRowsCount", {count: group.count})}
                </span>
            </div>
            <Input
                type="text"
                className="async-table-group-rule-name m-0"
                value={name}
                placeholder={placeholder ?? t("groupRuleNamePlaceholder")}
                optionalProps={{
                    "aria-label": t("groupRuleNameLabel", {label}),
                }}
                onChange={onNameChange}
            />
        </li>
    );
}

/**
 * @param {Object} props.preview grupy z podglądu API po kluczu (String) i ostrzeżenia po kluczu
 */
function GroupsPreview({parsed, names, setName, rule, preview}) {
    const {t} = useTranslation();
    // nic poprawnego nie wpisano - "Pozostałe" i "(brak)" same nie mają sensu
    if (parsed.labels.length === 0) {
        return <RuleInfo text={t("groupRuleEmptyPreview")} />;
    }
    return (
        <>
            <div className="async-table-group-rule-caption">
                {t("groupRuleGroupsPreview", {
                    count:
                        parsed.labels.length +
                        (rule.rest ? 1 : 0) +
                        (rule.nullGroup ? 1 : 0),
                })}
            </div>
            {/* liczby wierszy w drodze - lista zostaje edytowalna, liczby przyciemnione */}
            <ul
                className={clsx("async-table-group-rule-groups", {
                    refreshing: preview.loading,
                })}>
                {parsed.labels.map((label, index) => (
                    <PreviewGroup
                        key={index}
                        label={label}
                        name={names[index] ?? ""}
                        onNameChange={(value) => setName(index, value)}
                        swatch={<GroupSwatch kind="bucket" index={index} />}
                        group={preview.groups[String(index)]}
                        warnings={preview.warnings[String(index)]}
                    />
                ))}
                {rule.rest && (
                    <PreviewGroup
                        label={t("grid.rest")}
                        swatch={<GroupSwatch kind="rest" />}
                        name={rule.restName}
                        onNameChange={rule.setRestName}
                        group={preview.groups[String(parsed.labels.length)]}
                        warnings={
                            preview.warnings[String(parsed.labels.length)]
                        }
                        // bez nazwy grupa wyświetli się samą etykietą
                        placeholder={t("groupRuleDefaultNamePlaceholder", {
                            name: t("grid.rest"),
                        })}
                        muted
                    />
                )}
                {rule.nullGroup && (
                    <PreviewGroup
                        label={t("emptyGroupValue")}
                        swatch={<GroupSwatch kind="empty" />}
                        name={rule.nullName}
                        onNameChange={rule.setNullName}
                        group={preview.groups[String(parsed.labels.length + 1)]}
                        placeholder={t("groupRuleDefaultNamePlaceholder", {
                            name: t("emptyGroupValue"),
                        })}
                        muted
                    />
                )}
            </ul>
        </>
    );
}

/**
 * Konfiguracja jednego poziomu grupowania: "Każda wartość" (zwykłe grupowanie) albo "Własne grupy" -
 * wyrażenia jak w filtrze kolumny, "|" oddziela grupy. Zastosowanie zwraca regułę (albo null - każda wartość).
 * Poziom usuwa się × na chipie, modal nie ma przycisku usuwania.
 * @param {Object} props
 * @param {Object} props.column nagłówek kolumny poziomu (name, filterType)
 * @param {Object} [props.rule] bieżąca reguła poziomu
 * @param {"asc"|"desc"|"count"} [props.sort] kolejność grup przy "Każdej wartości"
 * @param {"day"|"week"|"month"} [props.dateUnit] okres grupowania kolumny z datą przy "Każdej wartości"
 * @param {number} props.level numer poziomu (od 1)
 * @param {number} props.levelsCount liczba poziomów grupowania
 * @param {function(Object|null, string, string)} props.onApply reguła (null - każda wartość), kolejność grup
 *        i okres (kolumna z datą)
 * @param {function} props.onClose
 * @param {Object} [props.distribution] dane do rozkładu i podglądu grup (endpoint distribution); brak - bez podglądu
 * @param {string} props.distribution.resultId
 * @param {Array} [props.distribution.filter] filtr tabeli w formacie API
 * @param {Array} props.distribution.groupFields poziomy `GroupFields` z konfiguracji tabeli
 * @param {Object} [props.distribution.parentColumn] nagłówek kolumny poziomu wyżej
 * @param {Object} [props.distribution.parentRule] reguła poziomu wyżej ("Własne grupy") - etykiety jego grup
 * @param {Object} [props.distribution.childColumn] nagłówek kolumny poziomu niżej
 */
export default function AsyncTableGroupRuleModal({
    column,
    rule,
    sort: initialSort,
    dateUnit: initialDateUnit,
    level,
    levelsCount,
    onApply,
    onClose,
    distribution,
}) {
    const {t} = useTranslation();
    // poziom w API liczony od 0
    const levelIndex = level - 1;

    const [mode, setMode] = useState(rule ? MODE_CUSTOM : MODE_EACH);
    const [sort, setSort] = useState(normalizeGroupSort(initialSort));
    const [dateUnit, setDateUnit] = useState(
        normalizeDateUnit(initialDateUnit)
    );

    // kolumna z datą grupuje "każdą wartość" po dniu, tygodniu albo miesiącu
    const dateLevel = isDateLevel(column.filterType);

    const dateUnitOptions = useMemo(
        () =>
            DATE_UNITS.map((value) => ({
                value,
                name: t(DATE_UNIT_LABELS[value]),
            })),
        [t]
    );
    const [expression, setExpression] = useState(rule?.expression ?? "");
    const [names, setNames] = useState(rule?.names ?? []);
    const [rest, setRest] = useState(rule?.rest ?? true);
    const [nullGroup, setNullGroup] = useState(rule?.nullGroup ?? false);
    const [restName, setRestName] = useState(rule?.restName ?? "");
    const [nullName, setNullName] = useState(rule?.nullName ?? "");

    const parsed = useMemo(
        () => parseGroupRule(column.filterType, expression),
        [column.filterType, expression]
    );

    // kolumny słownikowe (lista wartości w filtrze) nie mają wyrażeń
    const customDisabled = parsed.errors.some(
        (error) => error.reason === "unsupported"
    );

    const setName = useCallback(
        (index, value) =>
            setNames((prev) => {
                const next = prev.slice();
                next[index] = value;
                return next;
            }),
        []
    );

    const invalid =
        mode === MODE_CUSTOM &&
        (parsed.errors.length > 0 || parsed.buckets.length === 0);

    // grupa poziomu wyżej, do której zawężony jest rozkład (null - wszystkie)
    const [parentKey, setParentKey] = useState(null);
    // kolory grup nadrzędnych zamiast grup wyrażenia
    const [showParents, setShowParents] = useState(false);

    const field = column.filterColumn || column.field;

    // robocza wersja poziomu - błędnego wyrażenia nie wysyłamy (podgląd zostaje ostatni)
    const workingLevel = useMemo(
        () =>
            buildGroupLevel(
                field,
                column.filterType,
                mode === MODE_CUSTOM && !invalid
                    ? {expression, rest, nullGroup}
                    : undefined,
                dateUnit,
                column.inputConverter
            ),
        [
            field,
            column.filterType,
            mode,
            invalid,
            expression,
            rest,
            nullGroup,
            dateUnit,
            column.inputConverter,
        ]
    );

    const tableGroupFields = distribution?.groupFields ?? EMPTY_GROUP_FIELDS;
    const workingGroupFields = useMemo(
        () =>
            tableGroupFields.map((item, index) =>
                index === levelIndex ? workingLevel : item
            ),
        [tableGroupFields, levelIndex, workingLevel]
    );

    const groupSort = useMemo(
        () => (mode === MODE_EACH ? buildGroupSort(sort, column) : undefined),
        [mode, sort, column]
    );

    const {profile, preview, previewLevel, loading, error} =
        useGroupDistribution({
            resultId: distribution?.resultId,
            filter: distribution?.filter,
            groupFields: workingGroupFields,
            level: levelIndex,
            parentKey,
            groupSort,
            previewEnabled: !invalid,
            typing: expression,
        });

    // podgląd z poprzedniego trybu nie opisuje bieżących grup
    const previewMode = previewLevel?.Buckets ? MODE_CUSTOM : MODE_EACH;
    const currentPreview =
        preview && !invalid && previewMode === mode ? preview : null;

    const bucketsCount = parsed.labels.length;

    // grupy podglądu po kluczu (tekst) - lista grup w modalu ma etykiety z wyrażenia
    const previewByKey = useMemo(() => {
        const groups = {};
        (currentPreview?.Groups ?? []).forEach((group) => {
            groups[String(group.key)] = group;
        });
        return groups;
    }, [currentPreview]);

    const groupLabel = useCallback(
        (key) => {
            if (mode === MODE_EACH) {
                const group = previewByKey[String(key)];
                return group ? formatGroupLabel(group.label, column, t) : "";
            }
            const index = Number(key);
            if (index < bucketsCount) return parsed.labels[index];
            return t(index === bucketsCount ? "grid.rest" : "emptyGroupValue");
        },
        [mode, previewByKey, column, t, bucketsCount, parsed.labels]
    );

    const parentLabel = useCallback(
        (key) => {
            const group = profile?.ParentGroups?.find(
                (item) => String(item.key) === String(key)
            );
            return group
                ? formatParentGroupLabel(
                      group,
                      distribution?.parentColumn,
                      distribution?.parentRule,
                      t
                  )
                : "";
        },
        [profile, distribution, t]
    );

    // ostrzeżenia kolumny i podglądu; te z kluczem także przy grupie na liście
    const warnings = useMemo(() => {
        const byKey = {};
        const messages = [];
        // "Każda wartość" bez okresu daty - grupy po surowych wartościach kolumny
        const rawValues =
            mode === MODE_EACH && typeof workingLevel === "string";
        (profile?.ColumnWarnings ?? []).forEach((code) => {
            const message = formatColumnWarning(code, t, rawValues);
            if (message) messages.push(message);
        });
        (currentPreview?.Warnings ?? []).forEach((warning) => {
            const message = formatPreviewWarning(
                warning,
                {
                    groupLabel,
                    parentLabel,
                    childName: distribution?.childColumn?.name,
                },
                t
            );
            if (!message) return;
            messages.push(message);
            if (warning.key !== undefined) {
                const key = String(warning.key);
                byKey[key] = [...(byKey[key] ?? []), message];
            }
        });
        return {byKey, messages};
    }, [
        mode,
        workingLevel,
        profile,
        currentPreview,
        groupLabel,
        parentLabel,
        distribution,
        t,
    ]);

    const suggestions = useMemo(
        () => getUsableSuggestions(profile?.Suggestions, t),
        [profile, t]
    );

    // propozycja zmienia konfigurację dopiero po kliknięciu
    const onApplySuggestion = useCallback(
        (suggestion) => {
            if (suggestion.kind === "buckets") {
                setMode(MODE_CUSTOM);
                // przedziały z serwera w jednostce danych - wpis w jednostce kolumny
                setExpression(
                    serializeGroupBuckets(
                        column.filterType,
                        suggestion.buckets,
                        column.inputConverter
                    )
                );
                setRest(!!suggestion.rest);
                // nowe przedziały - stare nazwy nie pasują
                setNames([]);
                return;
            }
            setMode(MODE_EACH);
            if (suggestion.kind === "dateUnit") {
                setDateUnit(normalizeDateUnit(suggestion.DateUnit));
            }
        },
        [column.filterType, column.inputConverter]
    );

    // BarGroups[i] odpowiada Bars[i] - inna długość to odpowiedzi z różnych zapytań
    const barGroups =
        currentPreview?.BarGroups &&
        currentPreview.BarGroups.length === profile?.Bars?.length
            ? currentPreview.BarGroups
            : undefined;

    const parentGroups = profile?.ParentGroups ?? EMPTY_GROUP_FIELDS;
    const hasSplit = !!profile?.Bars?.some((bar) => bar.split);
    // jeden podział naraz: domyślnie grupy wyrażenia, grupy nadrzędne po zaznaczeniu
    let colorBy = "none";
    if (hasSplit && (showParents || !barGroups)) colorBy = "parents";
    else if (barGroups) colorBy = "groups";

    // nazwa segmentu słupka w podpowiedzi wykresu
    const segmentLabel = useCallback(
        (key) => {
            if (colorBy === "parents") return parentLabel(key);
            if (colorBy === "none") return t("rows");
            // wiersze spoza reguł mają klucz "Pozostałe" także bez tej grupy
            if (Number(key) === bucketsCount && !rest) {
                return t("groupDistributionOutside");
            }
            return groupLabel(key);
        },
        [colorBy, parentLabel, t, bucketsCount, rest, groupLabel]
    );

    const onConfirm = useCallback(() => {
        if (mode === MODE_EACH) {
            onApply(null, sort, dateUnit);
            return;
        }
        onApply(
            {
                expression: expression.trim(),
                // nazwy tylko dla istniejących przedziałów
                names: parsed.labels.map((_, index) => names[index] ?? ""),
                rest,
                restName,
                nullGroup,
                nullName,
            },
            sort,
            dateUnit
        );
    }, [
        sort,
        dateUnit,
        mode,
        onApply,
        expression,
        parsed.labels,
        names,
        rest,
        restName,
        nullGroup,
        nullName,
    ]);

    // "unsupported" nie jest błędem wpisu - tryb własnych grup jest wtedy wyłączony
    const errorMessages = parsed.errors
        .filter((error) => ERROR_KEYS[error.reason])
        .map((error) => ({
            index: error.index,
            text: t(ERROR_KEYS[error.reason], {
                segment: error.segment,
                max: MAX_RULE_GROUPS,
            }),
        }));

    return (
        <Modal
            show
            onHide={onClose}
            size="lg"
            className="async-table-group-rule-modal">
            <ModalHeader
                title={t("groupingByColumn", {name: column.name})}
                onCloseClick={onClose}
            />
            <ModalBody>
                {levelsCount > 1 && (
                    <div className="async-table-group-rule-level">
                        {t("groupLevelOf", {level, total: levelsCount})}
                    </div>
                )}
                <Row className="gy-3">
                    <Col xs={12} md={6}>
                        <TextSwitch
                            value={mode}
                            onChange={setMode}
                            options={[
                                {
                                    value: MODE_EACH,
                                    name: t("allValues"),
                                },
                                {
                                    value: MODE_CUSTOM,
                                    name: t("groupRuleModeCustom"),
                                    disabled: customDisabled,
                                },
                            ]}
                        />
                        {customDisabled && (
                            <RuleInfo text={t("groupRuleCustomUnsupported")} />
                        )}
                        {mode === MODE_CUSTOM ? (
                            <>
                                <LabeledInput
                                    id="async-table-group-rule-expression"
                                    type="text"
                                    label={t("groupRuleExpressionLabel")}
                                    value={expression}
                                    onChange={setExpression}
                                    error={
                                        errorMessages
                                            .map((error) => error.text)
                                            .join(" ") || undefined
                                    }
                                />
                                {errorMessages.length > 0 ? (
                                    <p
                                        className="async-table-group-rule-error"
                                        role="alert">
                                        {errorMessages.map((error) => (
                                            <span key={error.index}>
                                                {error.text}
                                            </span>
                                        ))}
                                    </p>
                                ) : (
                                    <p className="async-table-group-rule-hint">
                                        {t("groupRuleExpressionExamples")}
                                    </p>
                                )}
                                {/* opcje przy wyrażeniu - podgląd po prawej rośnie i maleje, opcje zostają w miejscu */}
                                <div>
                                    <Checkbox
                                        id="async-table-group-rule-rest"
                                        label={t("groupRuleShowRest")}
                                        checked={rest}
                                        onChange={setRest}
                                    />
                                    <Checkbox
                                        id="async-table-group-rule-null"
                                        label={t("groupRuleEmptyGroup")}
                                        checked={nullGroup}
                                        onChange={setNullGroup}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                {dateLevel && (
                                    <LabeledSelect
                                        id="async-table-group-rule-date-unit"
                                        label={t("groupByDateUnit")}
                                        value={dateUnit}
                                        onChange={setDateUnit}
                                        options={dateUnitOptions}
                                        clearButton={false}
                                    />
                                )}
                                <LabeledSelect
                                    id="async-table-group-rule-sort"
                                    label={t("groupRuleSortLabel")}
                                    value={sort}
                                    onChange={setSort}
                                    options={GROUP_SORTS.map((value) => ({
                                        value,
                                        name: t(GROUP_SORT_LABELS[value]),
                                    }))}
                                    customRenderer={renderSortOption}
                                    clearButton={false}
                                />
                            </>
                        )}
                        <Suggestions
                            items={suggestions}
                            onApply={onApplySuggestion}
                        />
                        {/* przy "Każdej wartości" rozkład pokazuje lista grup - histogram byłby tym samym */}
                        {mode === MODE_CUSTOM && distribution && (
                            <>
                                {levelIndex > 0 && parentGroups.length > 0 && (
                                    <ParentSwitch
                                        parentGroups={parentGroups}
                                        value={parentKey}
                                        onChange={setParentKey}
                                        parentColumn={distribution.parentColumn}
                                        groupLabel={parentLabel}
                                        showParents={showParents}
                                        onShowParentsChange={
                                            hasSplit && barGroups
                                                ? setShowParents
                                                : undefined
                                        }
                                    />
                                )}
                                <DistributionArea
                                    loading={loading || (!profile && !error)}
                                    error={error}>
                                    {profile && (
                                        <DistributionChart
                                            profile={profile}
                                            barGroups={barGroups}
                                            colorBy={colorBy}
                                            bucketsCount={bucketsCount}
                                            column={column}
                                            segmentLabel={segmentLabel}
                                        />
                                    )}
                                </DistributionArea>
                                {colorBy === "parents" && (
                                    <ParentLegend
                                        parentGroups={parentGroups}
                                        parentLabel={parentLabel}
                                    />
                                )}
                            </>
                        )}
                    </Col>
                    <Col xs={12} md={6}>
                        {mode === MODE_CUSTOM && (
                            <>
                                <GroupsPreview
                                    parsed={parsed}
                                    names={names}
                                    setName={setName}
                                    rule={{
                                        rest,
                                        restName,
                                        setRestName,
                                        nullGroup,
                                        nullName,
                                        setNullName,
                                    }}
                                    preview={{
                                        groups: previewByKey,
                                        warnings: warnings.byKey,
                                        loading,
                                    }}
                                />
                            </>
                        )}
                        {mode === MODE_EACH && distribution && (
                            <DistributionArea
                                loading={loading || (!currentPreview && !error)}
                                error={error}>
                                {currentPreview && (
                                    <EachValuePreview
                                        preview={currentPreview}
                                        column={column}
                                        childColumn={distribution.childColumn}
                                    />
                                )}
                            </DistributionArea>
                        )}
                        <DistributionWarnings messages={warnings.messages} />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter
                onCloseClick={onClose}
                closeText={t("cancel")}
                hasConfirmButton
                confirmText={t("apply")}
                confirmButtonColor="success"
                onConfirmed={onConfirm}
                hasButtonDisabled={invalid}
            />
        </Modal>
    );
}
